/**
 * The token gate's LOGIC, shared by both products' gates.
 *
 * There is one authentication mechanism — a shared bearer token in
 * localStorage, validated by making a real request — and there must stay
 * exactly one. The fleet gate and the modeller gate differ only in what they
 * look like and what they say; forking this would give the two surfaces two
 * ways to be wrong about the same token.
 *
 * NOT a user/session system, and neither gate should imply one. There is no
 * account, no password and no identity: a token either works or it does not.
 */

import { useState } from 'react';
import { ApiError, api, setToken, setWriteToken, verifyWriteToken } from './client';

export interface TokenGate {
  value: string;
  setValue: (v: string) => void;
  /** The optional second credential. See `writeNote` for why there are two. */
  writeValue: string;
  setWriteValue: (v: string) => void;
  error: string | null;
  busy: boolean;
  submit: (event: { preventDefault: () => void }) => Promise<void>;
}

export function useTokenGate(onAuthenticated: () => void): TokenGate {
  const [value, setValue] = useState('');
  const [writeValue, setWriteValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: { preventDefault: () => void }) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setToken(value.trim());
    setWriteToken('');
    try {
      /* A real request is the only honest test of a token. `/v1/fleet` is the
         one both products can reach; it validates the credential, not the
         product. */
      await api.fleet();

      /* The second credential, if one was given. Checked HERE rather than at
         the first write: a token accepted at sign-in and refused later would
         put the failure a long way from its cause. */
      const w = writeValue.trim();
      if (w) {
        if (await verifyWriteToken(w)) {
          setWriteToken(w);
        } else {
          setToken('');
          setError(
            'That second credential was not accepted for changes. Leave it '
            + 'blank to continue with viewing only.',
          );
          return;
        }
      }
      onAuthenticated();
    } catch (err) {
      /* A token that failed is not kept. Leaving it in storage would make the
         next page load look authenticated and fail everywhere instead. */
      setToken('');
      setWriteToken('');
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return { value, setValue, writeValue, setWriteValue, error, busy, submit };
}
